import { createMongoAbility } from '@casl/ability'
import type { Router } from 'vue-router'
import { authReady } from '@/utils/firebaseAuth'
import type { Actions, Rule, Subjects } from '@/plugins/casl/ability'

/**
 * The ability for the current session, rebuilt from the cookie.
 *
 * Not `useAbility()`: that resolves through `inject`, which is only reliable inside
 * a component's setup, and a navigation guard is neither. The cookie is the same
 * source `plugins/casl/index.ts` installs the app-wide ability from, so the two
 * cannot drift.
 */
const sessionAbility = () =>
  createMongoAbility<[Actions, Subjects]>(
    useCookie<Rule[]>('userAbilityRules').value ?? [],
  )

export const setupGuards = (router: Router) => {
  // 👉 router.beforeEach
  // Docs: https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards
  router.beforeEach(async to => {
    // Wait for the first onIdTokenChanged fire so cookies reflect Firebase
    // state — eliminates the cold-load race where a >1h-old cookie tricks the
    // guard into admitting the user with no live token in memory.
    await authReady

    const token = useCookie('accessTokenOperi').value
    const userData = useCookie<{ roles?: string[] } | null>('userData').value

    // A back-office session is a token plus at least one role that gets you in.
    // Which role decides what you can reach, and that is CASL's business below.
    const isLoggedIn = Boolean(token) && (userData?.roles?.length ?? 0) > 0

    /*
     * If it's a public route, continue navigation. This kind of pages are allowed to visited by login & non-login users. Basically, without any restrictions.
     * Examples of public routes are, 404, under maintenance, etc.
     */
    if (to.meta.public)
      return

    /*
      If user is logged in and is trying to access login like page, redirect to home
      else allow visiting the page
      (WARN: Don't allow executing further by return statement because next code will check for permissions)
     */
    if (to.meta.unauthenticatedOnly) {
      if (isLoggedIn)
        return '/'
      else return undefined
    }

    if (!isLoggedIn) {
      return {
        name: 'login',
        query: {
          ...to.query,
          to: to.fullPath !== '/' ? to.path : undefined,
        },
      }
    }

    // An untagged page is an admin page. Default-deny, so adding a page to the back
    // office does not accidentally expose it to drivers, and only the handful of
    // driver routes need a tag. An admin holds `manage all` and so passes every
    // untagged page; the driving pages are the exception, carved back out for an
    // admin who does not also hold DRIVER.
    const action = (to.meta.action as Actions | undefined) ?? 'manage'
    const subject = (to.meta.subject as Subjects | undefined) ?? 'AdminArea'

    if (!sessionAbility().can(action, subject))
      return { name: 'not-authorized' }
  })
}
